import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { Plus, Edit, Trash2, Upload, Calendar as CalendarIcon, MapPin, Users, Banknote } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEventsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'workshop',
    date: '',
    end_date: '',
    location: '',
    price: 0,
    max_participants: 10,
    image_url: '',
    is_active: true
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [eventsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/events/all`, { headers }),
        axios.get(`${API}/bookings`, { headers })
      ]);
      setEvents(eventsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title,
        description: event.description,
        type: event.type,
        date: event.date.slice(0, 16),
        end_date: event.end_date ? event.end_date.slice(0, 16) : '',
        location: event.location,
        price: event.price,
        max_participants: event.max_participants,
        image_url: event.image_url || '',
        is_active: event.is_active
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        description: '',
        type: 'workshop',
        date: '',
        end_date: '',
        location: '',
        price: 0,
        max_participants: 10,
        image_url: '',
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEvent(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const response = await axios.post(
          `${API}/upload/base64`,
          { image: reader.result },
          { headers: getAuthHeaders() }
        );
        setFormData({ ...formData, image_url: `${BACKEND_URL}${response.data.url}` });
        toast.success('Image uploadée');
      } catch (error) {
        toast.error("Erreur lors de l'upload");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      date: new Date(formData.date).toISOString(),
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
    };

    try {
      if (editingEvent) {
        await axios.put(
          `${API}/events/${editingEvent.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Événement mis à jour');
      } else {
        await axios.post(
          `${API}/events`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Événement créé');
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;

    try {
      await axios.delete(`${API}/events/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Événement supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredEvents = events.filter(event => {
    if (activeTab === 'all') return true;
    return event.type === activeTab;
  });

  const getEventBookings = (eventId) => {
    return bookings.filter(b => b.event_id === eventId);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-[#3D2E35]">Événements & Workshops</h1>
            <p className="text-[#6B5A62] mt-1">Gérez vos événements et ateliers floraux</p>
          </div>
          <Button
            onClick={() => handleOpenModal()}
            className="btn-tevaite rounded-xl text-white"
          >
            <Plus size={20} className="mr-2" />
            Créer un événement
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white">
            <TabsTrigger value="all">Tous</TabsTrigger>
            <TabsTrigger value="workshop">Workshops</TabsTrigger>
            <TabsTrigger value="evenement">Événements</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Events List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#8B4B7C] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl">
            <CalendarIcon className="mx-auto text-[#E8A5C4]" size={48} />
            <p className="text-[#6B5A62] mt-4">Aucun événement pour le moment</p>
            <Button
              onClick={() => handleOpenModal()}
              className="mt-4 btn-tevaite rounded-xl text-white"
            >
              Créer votre premier événement
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEvents.map((event) => {
              const eventBookings = getEventBookings(event.id);
              const spotsLeft = event.max_participants - event.current_participants;
              const isPast = new Date(event.date) < new Date();
              
              return (
                <div key={event.id} className={`bg-white rounded-2xl overflow-hidden shadow-sm ${isPast ? 'opacity-60' : ''}`}>
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="w-full md:w-48 h-40 md:h-auto flex-shrink-0 bg-gradient-to-br from-[#E8A5C4]/20 to-[#C45B9F]/20 relative overflow-hidden">
                      {event.image_url ? (
                        <img
                          src={event.image_url}
                          alt=""
                          className="w-full h-full object-cover absolute inset-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : null}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <CalendarIcon className="text-[#C45B9F]/30" size={40} />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              event.type === 'workshop' 
                                ? 'bg-[#5A8F6E]/10 text-[#5A8F6E]'
                                : 'bg-[#8B4B7C]/10 text-[#8B4B7C]'
                            }`}>
                              {event.type === 'workshop' ? 'Workshop' : 'Événement'}
                            </span>
                            {!event.is_active && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Inactif
                              </span>
                            )}
                            {isPast && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Passé
                              </span>
                            )}
                          </div>
                          <h3 className="font-heading text-xl text-[#3D2E35]">{event.title}</h3>
                          <p className="text-[#6B5A62] mt-2 line-clamp-2">{event.description}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleOpenModal(event)}
                            className="p-2 rounded-lg hover:bg-[#FAF8F5] transition-colors"
                          >
                            <Edit size={18} className="text-[#8B4B7C]" />
                          </button>
                          <button
                            onClick={() => handleDelete(event.id)}
                            className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <Trash2 size={18} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Details */}
                      <div className="flex flex-wrap gap-4 mt-4 text-sm text-[#6B5A62]">
                        <div className="flex items-center">
                          <CalendarIcon size={16} className="mr-2 text-[#C45B9F]" />
                          {formatDate(event.date)}
                        </div>
                        <div className="flex items-center">
                          <MapPin size={16} className="mr-2 text-[#C45B9F]" />
                          {event.location}
                        </div>
                        <div className="flex items-center">
                          <Users size={16} className="mr-2 text-[#C45B9F]" />
                          {event.current_participants}/{event.max_participants} ({spotsLeft} places restantes)
                        </div>
                        <div className="flex items-center">
                          <Banknote size={16} className="mr-2 text-[#C45B9F]" />
                          {event.price.toLocaleString()} XPF
                        </div>
                      </div>

                      {/* Bookings */}
                      {eventBookings.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-[#E8A5C4]/20">
                          <p className="text-sm font-medium text-[#3D2E35] mb-2">
                            Réservations ({eventBookings.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {eventBookings.slice(0, 5).map((booking) => (
                              <span 
                                key={booking.id}
                                className={`px-2 py-1 rounded text-xs ${
                                  booking.payment_status === 'paid'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}
                              >
                                {booking.name} ({booking.participants})
                              </span>
                            ))}
                            {eventBookings.length > 5 && (
                              <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600">
                                +{eventBookings.length - 5} autres
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#3D2E35]">
                {editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Image</Label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-[#E8A5C4]/50 rounded-xl p-4 text-center cursor-pointer hover:border-[#C45B9F] transition-colors"
                >
                  {formData.image_url ? (
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="py-4">
                      <Upload className="mx-auto text-[#C45B9F]" size={24} />
                      <p className="text-[#6B5A62] text-sm mt-2">Cliquez pour uploader</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Type */}
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="border-[#E8A5C4]/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="evenement">Événement</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Nom de l'événement"
                  required
                  className="border-[#E8A5C4]/30"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de l'événement"
                  required
                  rows={3}
                  className="border-[#E8A5C4]/30"
                />
              </div>

              {/* Date & End Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de début *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="border-[#E8A5C4]/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="border-[#E8A5C4]/30"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label>Lieu *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Adresse ou lieu"
                  required
                  className="border-[#E8A5C4]/30"
                />
              </div>

              {/* Price & Max Participants */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prix (XPF) *</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    min="0"
                    required
                    className="border-[#E8A5C4]/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Places max *</Label>
                  <Input
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
                    min="1"
                    required
                    className="border-[#E8A5C4]/30"
                  />
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center justify-between">
                <Label>Événement actif</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-tevaite text-white"
                >
                  {editingEvent ? 'Enregistrer' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminEventsPage;
